import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { RegisterSplitLayout } from '../../components/register/RegisterSplitLayout';
import { REGISTER_VISUALS } from '../../components/register/registerVisuals';
import { useRegistrationOnboardingGuard } from '../../lib/hooks/useRegistrationOnboardingGuard';
import { trackRegisterStepViewed } from '../../lib/analytics/onboardingEvents';
import { InviteRowsForm, type InviteRole, type InviteRowsFormHandle } from '../../components/settings/InviteRowsForm';

/**
 * Step 9 (ultimo): invita il team. Riusa il form inviti delle Impostazioni
 * (API core /api/invites già live) ma con CTA UNICA: se ci sono email da
 * inviare il bottone diventa "Invita e vai alla dashboard" (invia, e naviga
 * solo se non ci sono errori da mostrare). Skippabile; in uscita full reload
 * su / per il re-bootstrap di auth/permessi (org e ruoli nati durante il flusso).
 */
export function TeamInvitePage() {
  const { ready, orgId } = useRegistrationOnboardingGuard();
  const [roles, setRoles] = useState<InviteRole[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [hasEmails, setHasEmails] = useState(false);
  const [sending, setSending] = useState(false);
  const formRef = useRef<InviteRowsFormHandle>(null);

  useEffect(() => { trackRegisterStepViewed('team'); }, []);

  useEffect(() => {
    if (!orgId) return;
    supabase
      .from('roles')
      .select('id, name')
      .eq('organization_id', orgId)
      .order('name')
      .then(({ data }) => {
        setRoles(data || []);
        setLoadingRoles(false);
      });
  }, [orgId]);

  const finish = () => {
    // Full reload: la sessione è nata a org null e i permessi sono arrivati a
    // metà flusso — serve il re-bootstrap completo (pattern inviti OAuth).
    window.location.replace('/');
  };

  const handleCta = async () => {
    if (!hasEmails) {
      finish();
      return;
    }
    setSending(true);
    const { failed } = (await formRef.current?.submit()) ?? { sent: 0, failed: 0 };
    setSending(false);
    // Con errori restiamo sulla pagina: i messaggi sono inline sulle righe.
    if (failed === 0) finish();
  };

  if (!ready || loadingRoles) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#787774]" />
      </div>
    );
  }

  return (
    <RegisterSplitLayout visual={REGISTER_VISUALS.team} step={9}>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#37352F] mb-1">Invita il tuo team</h1>
        <p className="text-[#787774] text-sm">
          Flusia dà il meglio in squadra: invita i colleghi via email, sceglierai tu il loro ruolo.
        </p>
      </div>
      <InviteRowsForm ref={formRef} roles={roles} hideSubmit onHasEmailsChange={setHasEmails} />
      <div className="mt-8 space-y-3">
        <button type="button" onClick={handleCta} disabled={sending} className="notion-register-btn success w-full">
          {sending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Invio inviti...</span>
            </>
          ) : (
            <>
              <span>{hasEmails ? 'Invita e vai alla dashboard' : 'Vai alla dashboard'}</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
        <button
          type="button"
          onClick={finish}
          disabled={sending}
          className="w-full text-center text-sm text-[#787774] hover:text-[#37352F] transition-colors py-1.5"
        >
          Salta per ora
        </button>
      </div>
    </RegisterSplitLayout>
  );
}
